// components/PDFViewer/PDFViewer.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { pdfjs } from 'react-pdf';
import { Document, Page } from 'react-pdf';
import { getPresignedUrl } from '../../common/apiClient';
import './PDFViewer.css';
import axios from "axios";

// 👇 usa la versión de pdfjs instalada
pdfjs.GlobalWorkerOptions.workerSrc = 
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = ({
  s3Key,
  fileType,
  fileName = "document.pdf", // Función para obtener presigned URL
  width,
  height = "300px",
  showControls = false,
  showDownload = true,
  showFullscreen = true,
  className = "",
  onLoadSuccess,
  onLoadError,
  refreshInterval = 300000, // 5 minutos por defecto
  maxRetries = 3
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [actualScale, setActualScale] = useState(1.0); // ✅ Para mostrar el valor real
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentPresignedUrl, setCurrentPresignedUrl] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [urlExpired, setUrlExpired] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const blobUrlRef = useRef('');

  const getFolderKey = (path) => {
    // Saca todo después de la última barra
    const idx = path.lastIndexOf('/');
    if (idx === -1) return path; // no había barra
    return path.substring(0, idx); // devuelve solo la carpeta
  }

  // Función para obtener presigned URL
  // Función para obtener presigned URL y descargar el archivo
  const fetchPresignedUrl = useCallback(async () => {
    if (!s3Key || !fileType) return;

    try {
      setLoading(true);
      setError(null);

      // Paso 1: Obtener la presigned URL
      const presignedData = await getPresignedUrl({
        key: getFolderKey(s3Key),
        contentType: fileType,
        presigned_type: "get_object",
        file_name: fileName
      });

      if (!presignedData?.url) {
        throw new Error('No presigned URL received');
      }

      // Paso 2: Descargar el archivo usando la presigned URL
      const response = await axios.get(presignedData.url, {
        responseType: 'arraybuffer',// Importante para archivos PDF
        timeout: 30000, // 30 segundos timeout
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`📥 Download progress: ${percentCompleted}%`);
        }

      });



      if (response.status !== 200) {
        throw new Error(`Failed to download PDF: ${response.status}`);
      }

      // Paso 3: Crear un blob URL para react-pdf
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);

      // Limpiar URL anterior si existe
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = '';
      }
      // Convertir ArrayBuffer a Uint8Array
      const pdfArray = new Uint8Array(response.data);
      setPdfData(pdfArray);
      blobUrlRef.current = blobUrl;
      setCurrentPresignedUrl(presignedData.url);
      setUrlExpired(false);
      setRetryCount(0);
      setScale(0.75);
      setLoading(false)

    } catch (err) {
      console.error('Error fetching and downloading PDF:', err);

      // Manejo específico de errores
      if (err.code === 'ECONNABORTED') {
        setError('Request timeout: PDF file is too large or connection is slow');
      } else if (err.response?.status === 403) {
        setError('Access denied: PDF URL may have expired');
        setUrlExpired(true);
      } else if (err.response?.status === 404) {
        setError('PDF file not found in storage');
      } else {
        setError(`Failed to load PDF: ${err.message || 'Unknown error'}`);
      }

      setLoading(false);
    }
  }, [s3Key, fileType, fileName]);

  // Función para refrescar URL si expira
  const handleUrlExpired = useCallback(async () => {
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setUrlExpired(true);
      await fetchPresignedUrl();
    } else {
      setError('PDF access expired. Maximum retry attempts reached.');
      setLoading(false);
    }
  }, [retryCount, maxRetries, fetchPresignedUrl]);

  const cleanupResources = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = '';
    }
  }, []);

  // Efecto para limpiar recursos al desmontar
  useEffect(() => () => cleanupResources(), [cleanupResources]);

  // Efecto para cargar URL inicial
  useEffect(() => {
    if (s3Key || fileType) {
      fetchPresignedUrl();
    } else {
      setError('No PDF source provided');
      setLoading(false);
    }
  }, []);



  // Funciones de control del documento
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
    setUrlExpired(false);
    if (onLoadSuccess) {
      onLoadSuccess({ numPages });
    }
  };

  const onDocumentLoadError = async (error) => {
    console.error('PDF load error:', error);

    // Verificar si el error es por URL expirada
    if (error.name === 'UnexpectedResponseError' ||
      error.message?.includes('403') ||
      error.message?.includes('expired')) {
      await handleUrlExpired();
      return;
    }

    setLoading(false);
    setError('Error loading PDF document. Please try again.');

    if (onLoadError) {
      onLoadError(error);
    }
  };

  // Navegación entre páginas
  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages));
  };

  const goToPage = (page) => {
    const pageNum = parseInt(page);
    if (pageNum >= 1 && pageNum <= numPages) {
      setPageNumber(pageNum);
    }
  };

  // Control de zoom
  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  // Pantalla completa
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const exitFullscreen = () => {
    setIsFullscreen(false);
  };

  // Descargar PDF desde S3
  const downloadPDF = async () => {
    try {
      let downloadUrl = currentPresignedUrl;

      // Si no hay URL o está expirada, obtener una nueva
      if (!downloadUrl || urlExpired) {
        if (getPresignedUrl) {
          const presignedData = await getPresignedUrl({
            key: s3Key,
            contentType: fileType,
            presigned_type: "get_object",
            file_name: fileName
          });
          downloadUrl = presignedData?.url;
        }
      }

      if (downloadUrl) {
        // Si es una blob URL, usar directamente
        if (downloadUrl.startsWith('blob:')) {
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          // Si es presigned URL, descargar primero
          const response = await axios.get(downloadUrl, {
            responseType: 'blob',
            timeout: 30000
          });

          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Limpiar URL temporal
          URL.revokeObjectURL(url);
        }
      } else {
        throw new Error('No download URL available');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF. Please try again.');
    }
  };

  // Función para reintentar carga
  const retryLoad = () => {
    setRetryCount(0);
    setError(null);
    setLoading(true);
    fetchPresignedUrl();
  };

  // Función para refrescar URL manualmente
  const refreshUrl = () => {
    if (s3Key && fileType) {
      fetchPresignedUrl();
    }
  };

  // Manejo del escape para salir de pantalla completa
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        exitFullscreen();
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen]);

  // Renderizar contenido principal
  const renderPDFContent = () => {
    if (loading) {
      return (
        <div className="pdf-loading">
          <div className="pdf-spinner"></div>
          <span>{urlExpired ? 'Refreshing PDF access...' : 'Loading PDF from secure storage...'}</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="pdf-error">
          <div className="pdf-error-icon">📄❌</div>
          <h3>Unable to load PDF</h3>
          <p>{error}</p>
          <div className="pdf-error-actions">
            <button className="pdf-retry-btn" onClick={retryLoad}>
              Try Again ({maxRetries - retryCount} attempts left)
            </button>
          </div>
        </div>
      );
    }

    if (!currentPresignedUrl) {
      return (
        <div className="pdf-empty">
          <div className="pdf-empty-icon">📄</div>
          <span>No PDF access URL available</span>
          {currentPresignedUrl && (
            <button className="pdf-load-btn" onClick={fetchPresignedUrl}>
              Load PDF
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="pdf-document-container">
        <Document
          file={{
            url: currentPresignedUrl,
            httpHeaders: {
              'Cache-Control': 'no-cache',
              'Accept': 'application/pdf'
            },
            withCredentials: false
          }}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="pdf-loading">
              <div className="pdf-spinner"></div>
              <span>Loading PDF from S3...</span>
            </div>
          }
          error={
            <div className="pdf-error">
              <span>Error loading PDF from secure storage</span>
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            width={width}
            loading={<div className="pdf-page-loading">Loading page...</div>}
            error={<div className="pdf-page-error">Error loading page</div>}
          />
        </Document>
      </div>
    );
  };

  return (
    <>
      <div className={`pdf-viewer-container ${className} ${isFullscreen ? 'fullscreen' : ''}`}>
        {/* Header con controles */}
        {/* Header */}
        {showControls && (
          <div className="pdf-viewer-header">
            <div className="pdf-info">
              <span className="pdf-title">{fileName}</span>
              {numPages && (
                <span className="pdf-pages-info">
                  Page {pageNumber} of {numPages}
                </span>
              )}
              {s3Key && (
                <span className="pdf-s3-info">
                  📦 {fileType || 'S3'}: {s3Key}
                </span>
              )}
            </div>

            <div className="pdf-controls">
              {/* Controles de navegación */}
              <div className="pdf-navigation">
                <button
                  className="pdf-control-btn"
                  onClick={goToPrevPage}
                  disabled={pageNumber <= 1}
                  title="Previous page"
                >
                  ⬅️
                </button>

                <input
                  type="number"
                  className="pdf-page-input"
                  value={pageNumber}
                  onChange={(e) => goToPage(e.target.value)}
                  min={1}
                  max={numPages || 1}
                />

                <button
                  className="pdf-control-btn"
                  onClick={goToNextPage}
                  disabled={pageNumber >= numPages}
                  title="Next page"
                >
                  ➡️
                </button>
              </div>

              {/* Controles de zoom */}
              <div className="pdf-zoom-controls">
                <button
                  className="pdf-control-btn"
                  onClick={zoomOut}
                  disabled={scale <= 0.5}
                  title="Zoom out"
                >
                  🔍➖
                </button>

                <span className="pdf-zoom-level">
                  {Math.round(scale * 100)}%
                </span>

                <button
                  className="pdf-control-btn"
                  onClick={zoomIn}
                  disabled={scale >= 3.0}
                  title="Zoom in"
                >
                  🔍➕
                </button>

                <button
                  className="pdf-control-btn"
                  onClick={resetZoom}
                  title="Reset zoom"
                >
                  🎯
                </button>
              </div>

              {/* Acciones adicionales */}
              <div className="pdf-actions">
                {currentPresignedUrl && (
                  <button
                    className="pdf-action-btn pdf-refresh-btn"
                    onClick={refreshUrl}
                    title="Refresh PDF access"
                    disabled={loading}
                  >
                    🔄 Refresh
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Botones independientes */}
        {(showDownload || showFullscreen) && (
          <div className="pdf-independent-actions">
            {showDownload && (
              <button
                className="pdf-action-btn pdf-download-btn"
                onClick={downloadPDF}
                title="Download PDF"
                disabled={!currentPresignedUrl || loading}
              >
                💾 Download
              </button>
            )}

            {showFullscreen && (
              <button
                className="pdf-action-btn pdf-fullscreen-btn"
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit fullscreen" : ""}
              >
                {isFullscreen ? '↙️' : '↗️'} {isFullscreen ? 'Exit' : 'Fullscreen'}
              </button>
            )}
          </div>
        )}

        {/* Contenido del PDF */}
        <div
          className="pdf-viewer-content scrollable"
          style={{ height: isFullscreen ? '100vh' : height }}
        >
          {renderPDFContent()}
        </div>

        {/* Footer con información adicional */}
        {showControls && !isFullscreen && (
          <div className="pdf-viewer-footer">
            <div className="pdf-footer-info">
              <span>Zoom: {Math.round(scale * 100)}%</span>
              {numPages && <span>Total pages: {numPages}</span>}
              {urlExpired && <span className="pdf-url-warning">⚠️ URL refreshed</span>}
            </div>

            <div className="pdf-keyboard-hints">
              <small>Loaded from secure S3 storage • Use ← → keys to navigate</small>
            </div>
          </div>
        )}
      </div>

      {/* Overlay para pantalla completa */}
      {isFullscreen && <div className="pdf-fullscreen-overlay" onClick={exitFullscreen} />}
    </>
  );
};

export default PDFViewer;